import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { ROLES, type Role } from '@ready/shared';
import { useApi, type InviteCode } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/labels';
import { Button, Card, ErrorText, Field, Input, Select } from '@/components/ui';
import { useOrgContext } from './layout';

/** Admins' most frequent task: share a code or link, see what's in use, revoke. */
export function OrgInvitesPage() {
  const { org, canManage } = useOrgContext();
  const api = useApi();
  const queryClient = useQueryClient();
  const [role, setRole] = useState<Role>('learner');
  const [maxUses, setMaxUses] = useState('');
  const codes = useQuery({
    queryKey: ['inviteCodes', org.id],
    queryFn: () => api.inviteCodes(org.id),
    enabled: canManage,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['inviteCodes', org.id] });
  const create = useMutation({
    mutationFn: () =>
      api.createInviteCode(org.id, { role, maxUses: maxUses ? Number(maxUses) : undefined }),
    onSuccess: () => {
      setMaxUses('');
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeInviteCode(org.id, id),
    onSuccess: refresh,
  });

  if (!canManage) return <Navigate to=".." replace />;

  return (
    <div className="space-y-6">
      <Card title="New invite code">
        <form
          className="grid gap-4 sm:grid-cols-[12rem_10rem_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Field label="Joins as" htmlFor="code-role">
            <Select id="code-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.filter((r) => r !== 'owner').map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Max uses" htmlFor="code-uses">
            <Input
              id="code-uses"
              type="number"
              min={1}
              placeholder="Unlimited"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={create.isPending}>
            Create code
          </Button>
        </form>
        <ErrorText error={create.error} />
      </Card>

      <Card title="Active codes">
        {codes.data?.codes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active codes yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {codes.data?.codes.map((c) => (
              <CodeRow
                key={c.id}
                code={c}
                onRevoke={() => revoke.mutate(c.id)}
                revoking={revoke.isPending}
              />
            ))}
          </ul>
        )}
        <ErrorText error={revoke.error} />
      </Card>

      <p className="text-sm text-muted-foreground">
        Prefer automatic joining?{' '}
        <Link to="../settings" className="text-primary">
          Verify your email domain
        </Link>{' '}
        and people with that email join when they sign in.
      </p>
    </div>
  );
}

function CodeRow({
  code,
  onRevoke,
  revoking,
}: {
  code: InviteCode;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/join/${code.code}`;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
      <div className="space-y-0.5">
        <p className="font-mono text-base font-semibold tracking-widest">{code.code}</p>
        <p className="text-muted-foreground">
          Joins as {ROLE_LABELS[code.role].toLowerCase()} · used {code.uses}
          {code.maxUses ? ` of ${code.maxUses}` : ''} times
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(link);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              // Clipboard blocked; the code is visible to copy by hand.
            }
          }}
        >
          {copied ? (
            <Check className="size-4" aria-hidden />
          ) : (
            <Copy className="size-4" aria-hidden />
          )}
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <Button variant="ghost" disabled={revoking} onClick={onRevoke}>
          Revoke
        </Button>
      </div>
    </li>
  );
}
